import { store } from "../../../_Bootstrap/RoutingAndStore";
import UUID from "../../Generic/UUID";

export type AudioTrackId = string;

interface AudioCacheData {
    buffer: AudioBuffer;
    timeout?: ReturnType<typeof setTimeout>;
    preloaded: boolean;
}

export type AudioManagerCallback = () => void;

export enum AudioManagerEvents {
    Ready = "Ready",
    TrackCreated = "TrackCreated",
    TrackRemoved = "TrackRemoved",
}

export default class AudioManager {
    protected ready = false;

    protected audioContext!: AudioContext;
    protected masterTrack!: AudioTrack;

    protected tracks: Record<string, AudioTrack> = {};
    protected cache: Record<string, AudioCacheData> = {};

    protected listeners: Record<AudioManagerEvents, Set<AudioManagerCallback>> = {
        [AudioManagerEvents.Ready]       : new Set<AudioManagerCallback>(),
        [AudioManagerEvents.TrackCreated]: new Set<AudioManagerCallback>(),
        [AudioManagerEvents.TrackRemoved]: new Set<AudioManagerCallback>(),
    };

    private constructor() {
        this.startEvent = this.startEvent.bind(this);

        // Check to see if window doesn't exist or creating AudioContext is already allowed
        if(typeof window === "undefined" || navigator.userActivation?.hasBeenActive) {
            this.start();
        } else {
            window.addEventListener("keydown", this.startEvent);
            window.addEventListener("mousedown", this.startEvent);
            window.addEventListener("pointerdown", this.startEvent);
            window.addEventListener("pointerup", this.startEvent);
            window.addEventListener("touchend", this.startEvent);
        }
    }

    private startEvent(event: Event): void {
        if(!event.isTrusted) {
            return;
        }

        this.start();
        this.disconnectWindowListeners();
    }

    private start(): void {
        if(this.ready) {
            return;
        }

        this.audioContext = new AudioContext();

        this.masterTrack = new AudioTrack(
            "master",
            this.audioContext,
            "master"
        );
        this.masterTrack.getNode().connect(this.audioContext.destination);

        this.ready = true;
        this.callListeners(AudioManagerEvents.Ready);
    }

    private disconnectWindowListeners(): void {
        if(typeof window === "undefined") {
            return;
        }

        window.removeEventListener("keydown", this.startEvent);
        window.removeEventListener("mousedown", this.startEvent);
        window.removeEventListener("pointerdown", this.startEvent);
        window.removeEventListener("pointerup", this.startEvent);
        window.removeEventListener("touchend", this.startEvent);
    }

    public addListener(event: AudioManagerEvents, listener: AudioManagerCallback): void {
        this.listeners[event].add(listener);
    }

    public removeListener(event: AudioManagerEvents, listener: AudioManagerCallback): void {
        this.listeners[event].delete(listener);
    }

    protected callListeners(event: AudioManagerEvents): void {
        for(const listener of this.listeners[event]) {
            listener();
        }
    }

    /**
     * Loads the audio file from memory
     * @param url
     * @param expiry how long to hold the audio file in memory in milliseconds
     * @param isPreloading whether the function call for load sound is just preloading the sound
     */
    public async loadSound(url: string, expiry?: number, isPreloading = false): Promise<AudioBuffer> {
        if(!this.ready) {
            throw new Error("AudioManager has not finished setup");
        }

        if(!this.cache[url]) {
            const results = await fetch(url);
            const arrayBuffer = await results.arrayBuffer();

            const buffer = await this.audioContext.decodeAudioData(arrayBuffer);

            let timeout;

            if(typeof expiry === "number") {
                timeout = setTimeout(() => {
                    delete this.cache[url];
                }, expiry);
            }

            this.cache[url] = {
                preloaded: isPreloading,
                buffer,
                timeout,
            };

            return buffer;
        }

        const { buffer, timeout, preloaded } = this.cache[url];

        if(typeof expiry === "number") {
            // Only update a preloaded expiry time if it has an existing expiry time
            let updateTimeout = !preloaded;

            if(typeof timeout === "number") {
                if(preloaded) {
                    updateTimeout = true;
                }

                clearTimeout(timeout);
            }

            if(updateTimeout) {
                this.cache[url].timeout = setTimeout(() => {
                    delete this.cache[url];
                }, expiry);
            }
        }

        if(preloaded) {
            if(typeof expiry === "number" && typeof timeout === "number") {
                if(typeof timeout === "number") {
                    clearTimeout(timeout);
                }

                this.cache[url].timeout = setTimeout(() => {
                    delete this.cache[url];
                }, expiry);
            }
        } else {
            if(typeof expiry === "number") {
                if(typeof timeout === "number") {
                    clearTimeout(timeout);
                }

                this.cache[url].timeout = setTimeout(() => {
                    delete this.cache[url];
                }, expiry);
            }
        }

        if(typeof expiry !== "undefined" && typeof timeout === "number") {
            clearTimeout(timeout);

            this.cache[url].timeout = setTimeout(() => {
                delete this.cache[url];
            }, expiry);
        }

        return buffer;
    }

    /**
     * Play sound in given audio track (if not provided, master audio track is used)
     * @param sound
     * @param track
     * @param expiry how long to hold the audio file in memory in milliseconds
     */
    public async play(
        sound: string | AudioBuffer,
        track?: AudioTrack | AudioTrackId,
        expiry?: number
    ): Promise<void> {
        if(!this.ready) {
            throw new Error("AudioManager has not finished setup");
        }

        let buffer;

        if(typeof sound === "string") {
            buffer = await this.loadSound(sound, expiry);
        } else {
            buffer = sound;
        }

        const source = this.audioContext.createBufferSource();

        source.buffer = buffer;

        if(typeof track === "undefined") {
            source.connect(this.masterTrack.getNode());
        } else {
            let found: AudioTrack;

            if(typeof track === "string") {
                found = this.getTrack(track);
            } else {
                found = track;
            }

            source.connect(found.getNode());
        }

        source.start();
    }

    public createTrack(key?: string): AudioTrack {
        if(!this.ready) {
            throw new Error("AudioManager has not finished setup");
        }

        const id = UUID.generate();
        const track = new AudioTrack(
            id,
            this.audioContext,
            key
        );

        track.getNode().connect(this.masterTrack.getNode());
        this.tracks[id] = track;

        this.callListeners(AudioManagerEvents.TrackCreated);

        return track;
    }

    public getTrack(id: AudioTrackId): AudioTrack {
        if(!this.ready) {
            throw new Error("AudioManager has not finished setup");
        }

        if(typeof this.tracks[id] === "undefined") {
            throw new Error(`Cannot find track ${id}`);
        }

        return this.tracks[id];
    }

    public removeTrack(track: AudioTrackId | AudioTrack): void {
        if(!this.ready) {
            throw new Error("AudioManager has not finished setup");
        }

        const id = typeof track === "string" ? track : track.id;

        if(typeof this.tracks[id] === "undefined") {
            throw new Error(`Cannot find track ${id} to delete`);
        }

        const tempTrack = this.tracks[id];

        tempTrack.getNode().disconnect();

        delete this.tracks[id];

        tempTrack.callListeners(AudioTrackEvents.removed);
        this.callListeners(AudioManagerEvents.TrackRemoved);
    }

    public getMasterTrack(): AudioTrack {
        if(!this.ready) {
            throw new Error("AudioManager has not finished setup");
        }

        return this.masterTrack;
    }

    public getTracks(): Array<AudioTrack> {
        if(!this.ready) {
            throw new Error("AudioManager has not finished setup");
        }

        return Object.values(this.tracks);
    }

    public isReady(): boolean {
        return this.ready;
    }

    /**
     * Singleton instance of audio manager
     * @private
     */
    private static instance: AudioManager;
    public static getInstance(): AudioManager {
        if(typeof this.instance === "undefined") {
            this.instance = new AudioManager();
        }

        return this.instance;
    }
}

/**
 * Play a sound on the shopfront audio track
 * @param url
 */
export function playShopfrontSound(url: string): void {
    const audioManager = AudioManager.getInstance();

    if(!audioManager.isReady()) {
        return;
    }

    const systemTrackId = store.getState().audio.systemTrack;

    if(systemTrackId === null) {
        return;
    }

    const systemTrack = audioManager.getTrack(systemTrackId);

    void audioManager.play(url, systemTrack);
}

export enum AudioTrackEvents {
    update = "update",
    removed = "removed",
}

export class AudioTrack {
    private readonly node: GainNode;

    private muted = false;
    private volume = 1.0;

    protected listeners: Record<AudioTrackEvents, Set<AudioManagerCallback>> = {
        [AudioTrackEvents.removed]: new Set<AudioManagerCallback>(),
        [AudioTrackEvents.update] : new Set<AudioManagerCallback>(),
    };

    constructor(
        public readonly id: AudioTrackId,
        audioContext: AudioContext,
        protected readonly key?: string
    ) {
        this.node = audioContext.createGain();

        this.loadLocalStorage();

        if(this.muted) {
            this.node.gain.value = 0;
        } else {
            this.node.gain.value = this.volume;
        }
    }

    public addListener(event: AudioTrackEvents, listener: AudioManagerCallback): void {
        this.listeners[event].add(listener);
    }

    public removeListener(event: AudioTrackEvents, listener: AudioManagerCallback): void {
        this.listeners[event].delete(listener);
    }

    public callListeners(event: AudioTrackEvents): void {
        for(const listener of this.listeners[event]) {
            listener();
        }
    }

    /**
     * Loads the audio track data from local storage
     * @protected
     */
    private loadLocalStorage(): void {
        if(typeof this.key !== "string") {
            return;
        }

        // Load muted value for audio track
        const localMuted = localStorage.getItem(`audio_${this.key}_muted`);

        if(localMuted === null) {
            return;
        }

        if(localMuted !== "false" && localMuted !== "true") {
            return;
        }

        // Load volume value for audio track
        const localLevel = localStorage.getItem(`audio_${this.key}_volume`);

        if(localLevel === null) {
            return;
        }

        const parsedLevel = parseFloat(localLevel);

        if(isNaN(parsedLevel)) {
            return;
        }

        if(parsedLevel < 0 || parsedLevel > 1.0) {
            return;
        }

        this.muted = localMuted === "true";
        this.volume = parsedLevel;
    }

    /**
     * Updated the audio track settings in the local storage
     * @protected
     */
    protected updateLocalStorage(): void {
        if(typeof this.key !== "string") {
            return;
        }

        localStorage.setItem(`audio_${this.key}_muted`, this.muted ? "true" : "false");
        localStorage.setItem(`audio_${this.key}_volume`, this.volume.toString());
    }

    /**
     * Sets the volume of the audio track
     * @param volume between 0.0 and 1.0 inclusive
     */
    public setVolume(volume: number): void {
        if(volume < 0 || volume > 1) {
            throw new Error("Audio track volume must be between 0.0 and 1.0 inclusive");
        }

        this.volume = volume;
        this.node.gain.value = volume;
        this.muted = false;

        this.callListeners(AudioTrackEvents.update);

        this.updateLocalStorage();
    }

    /**
     * Mute the audio track
     */
    public mute(): void {
        this.volume = this.node.gain.value;
        this.node.gain.value = 0;
        this.muted = true;

        this.callListeners(AudioTrackEvents.update);

        this.updateLocalStorage();
    }

    /**
     * Unmute the audio track
     */
    public unmute(): void {
        this.node.gain.value = this.volume;
        this.muted = false;

        this.callListeners(AudioTrackEvents.update);

        this.updateLocalStorage();
    }

    /**
     * Toggle the mute on the audio track
     */
    public toggleMute(): void {
        if(this.muted) {
            this.unmute();
        } else {
            this.mute();
        }
    }

    public isMuted(): boolean {
        return this.muted;
    }

    public getLevel(): number {
        return this.volume;
    }

    public getNode(): AudioNode {
        return this.node;
    }
}
